"use client";

import {
  ArrowRightIcon,
  BarChart3,
  Bot,
  Users,
  BarChart2,
  Settings, 
} from "lucide-react";
import React from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { THome_Config } from "@/app/config";
import Link from "next/link";
import { motion } from "motion/react";

const featureIcons = [
  {
    icon: Settings,
  },
  {
    icon: Users,
  },
  {
    icon: BarChart2,
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.6,
      staggerChildren: 0.2
    }
  }
};


export const ZumaForIndustriesSection = ({config}: {config: THome_Config['industries']})=> {
  return (
    <motion.section
      className="w-full py-12 md:py-20 lg:py-[84px] bg-[#f8fafb] flex justify-center"
      id="industries"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-100px" }}
      variants={containerVariants}
    >
      <div className="max-w-[1120px] w-full px-4 md:px-6 lg:px-8">
        {/* Header Section */}
        <motion.div 
          className="flex flex-col items-center gap-4 md:gap-6 lg:gap-3.5 mb-8 md:mb-12 lg:mb-[49px]"
          variants={{
            hidden: { opacity: 0, y: 40 },
            visible: {
              opacity: 1,
              y: 0,
              transition: {
                duration: 0.7,
                ease: "easeOut"
              }
            }
          }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            whileHover={{ scale: 1.05 }}
          >
            <Badge className="bg-frail-scale-05242098figmasitemagnolia border-[#ffecd4] text-[#8200da] text-xs md:text-sm lg:text-[10.5px] leading-tight lg:leading-[14px] [font-family:'Segoe_UI_Symbol-Regular',Helvetica] font-normal px-3 py-1 lg:px-2 lg:py-[2.75px] rounded-lg lg:rounded-[6.75px]">
              {config.tag}
            </Badge>
          </motion.div>

          <motion.h1 
            className="text-frail-scale-05242098figmasiteebony text-2xl sm:text-3xl md:text-4xl lg:text-[42px] leading-tight lg:leading-[42px] text-center [font-family:'Segoe_UI_Symbol-Regular',Helvetica] font-normal tracking-[0] px-4 md:px-0"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.2 }}
          >
            {config.title}
          </motion.h1>

          <motion.div 
            className="max-w-2xl w-full px-4 md:px-0"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <p className="text-[#495565] text-sm md:text-base lg:text-[17.4px] leading-relaxed lg:leading-[24.5px] text-center [font-family:'Segoe_UI_Symbol-Regular',Helvetica] font-normal tracking-[0]">
              {config.description}
            </p>
          </motion.div>
        </motion.div>

        {/* Metrics and Integration Cards */}
        <motion.div 
          className="flex flex-col lg:flex-row gap-6 md:gap-8 lg:gap-[21px] mb-8 md:mb-12 lg:mb-7"
          variants={{
            visible: {
              transition: {
                staggerChildren: 0.2,
                delayChildren: 0.3
              }
            }
          }}
        >
          {/* Performance Metrics Card */}
          <motion.div
            variants={{
              hidden: { opacity: 0, y: 50, scale: 0.95 },
              visible: {
                opacity: 1,
                y: 0,
                scale: 1,
                transition: {
                  duration: 0.8,
                  ease: "easeOut"
                }
              },
              hover: {
                y: -8,
                scale: 1.02,
                transition: {
                  duration: 0.3,
                  ease: "easeInOut"
                }
              }
            }}
            whileHover="hover"
            className="flex-1"
          >
            <Card className="border-[#ffecd4] bg-[linear-gradient(170deg,rgba(249,250,251,1)_0%,rgba(243,232,255,0.5)_100%)] rounded-xl lg:rounded-[12.75px] h-full">
              <CardContent className="p-6 md:p-8 lg:p-7">
                <motion.div 
                  className="flex items-center justify-between mb-6 md:mb-8 lg:mb-[21px]"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                >
                  <h3 className="text-lg md:text-xl lg:text-[21px] leading-tight lg:leading-7 text-frail-scale-05242098figmasiteebony [font-family:'Segoe_UI_Symbol-Regular',Helvetica] font-normal tracking-[0]">
                    {config.PerformanceTitle}
                  </h3>
                  <motion.div
                    variants={{
  animate: {
    scale: [1, 1.05, 1],
    transition: {
      duration: 2,
      ease: "easeInOut",
      repeat: Infinity,
      repeatDelay: 1
    }
  }
}}
                    animate="animate"
                    whileHover={{ 
                      rotate: 15,
                      scale: 1.1,
                      transition: { duration: 0.2 }
                    }}
                  >
                    <BarChart3 className="w-6 h-6 md:w-7 md:h-7 text-[#8200da]" />
                  </motion.div>
                </motion.div>

                <motion.div 
                  className="grid grid-cols-2 gap-4 md:gap-6 lg:gap-x-[21px] lg:gap-y-[41px]"
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={{
                    visible: {
                      transition: {
                        staggerChildren: 0.1,
                        delayChildren: 0.3
                      }
                    }
                  }}
                >
                  {config.performanceMetrics.map((metric, index) => (
                    <motion.div
                      key={index}
                      className="flex flex-col gap-1 md:gap-2 lg:gap-[2.5px]"
                      variants={{
                        hidden: { opacity: 0, scale: 0.5 },
                        visible: {
                          opacity: 1,
                          scale: 1,
                          transition: {
                            duration: 0.6,
                            ease: "easeOut",
                            type: "spring",
                            stiffness: 200
                          }
                        }
                      }}
                      whileHover={{ 
                        scale: 1.05,
                        transition: { duration: 0.2 }
                      }}
                    >
                      <motion.div
                        className={`${metric.color} text-xl md:text-2xl lg:text-[26.3px] leading-tight lg:leading-[31.5px] [font-family:'Segoe_UI_Symbol-Regular',Helvetica] font-normal tracking-[0]`}
                        initial={{ scale: 0, opacity: 0 }}
                        whileInView={{ scale: 1, opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ 
                          duration: 0.8, 
                          delay: index * 0.1 + 0.5,
                          type: "tween",
                          stiffness: 200
                        }}
                      >
                        {metric.value}
                      </motion.div>
                      <motion.div 
                        className="text-[#495565] text-xs md:text-sm lg:text-[12.3px] leading-tight lg:leading-[17.5px] [font-family:'Segoe_UI_Symbol-Regular',Helvetica] font-normal tracking-[0]"
                        initial={{ opacity: 0, y: 10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: index * 0.1 + 0.7 }}
                      >
                        {metric.label}
                      </motion.div>
                    </motion.div>
                  ))}
                </motion.div>
              </CardContent>
            </Card>
          </motion.div>

          {/* AI Integration Card */}
          <motion.div
            variants={{
              hidden: { opacity: 0, y: 50, scale: 0.95 },
              visible: {
                opacity: 1,
                y: 0,
                scale: 1,
                transition: {
                  duration: 0.8,
                  ease: "easeOut"
                }
              },
              hover: {
                y: -8,
                scale: 1.02,
                transition: {
                  duration: 0.3,
                  ease: "easeInOut"
                }
              }
            }}
            whileHover="hover"
            className="flex-1"
          >
            <Card className="bg-white rounded-xl lg:rounded-[12.75px] border h-full">
              <CardContent className="p-6 md:p-8 lg:p-7">
                <motion.div 
                  className="flex items-center gap-3 md:gap-4 lg:gap-3.5 mb-6 md:mb-8 lg:mb-[21px]"
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                >
                  <motion.div 
                    className="w-10 h-10 md:w-11 md:h-11 lg:w-[42px] lg:h-[42px] flex items-center justify-center bg-frail-scale-05242098figmasitezircon rounded-lg lg:rounded-[8.75px] flex-shrink-0"
                    variants={{
                      animate: {
                        y: [-2, 2, -2],
                        transition: {
                          duration: 3,
                          ease: "easeInOut",
                          repeat: Infinity
                        }
                      }
                    }}
                    animate="animate"
                    whileHover={{ 
                      scale: 1.1,
                      rotate: 5,
                      transition: { duration: 0.2 }
                    }}
                  >
                    <Bot className="w-5 h-5 md:w-6 md:h-6 lg:w-[21px] lg:h-[21px] text-[#155cfb]" />
                  </motion.div>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-frail-scale-05242098figmasiteebony text-base md:text-lg lg:text-[17.5px] leading-tight lg:leading-[24.5px] [font-family:'Segoe_UI_Symbol-Regular',Helvetica] font-normal tracking-[0]">
                      {config.IntegrationTitle}
                    </h3>
                    <p className="text-[#495565] text-sm md:text-base lg:text-sm leading-tight lg:leading-[21px] [font-family:'Segoe_UI_Symbol-Regular',Helvetica] font-normal tracking-[0]">
                      {config.IntegrationDescription}
                    </p>
                  </div>
                </motion.div>

                <motion.div 
                  className="flex flex-col gap-3 md:gap-4 lg:gap-[10.5px]"
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={{
                    visible: {
                      transition: {
                        staggerChildren: 0.1,
                        delayChildren: 0.4
                      }
                    }
                  }}
                >
                  {config.integrationStatuses.map((item, index) => (
                    <motion.div
                      key={index}
                      className="flex items-center justify-between"
                      variants={{
                        hidden: { opacity: 0, x: -20 },
                        visible: {
                          opacity: 1,
                          x: 0,
                          transition: {
                            duration: 0.5,
                            ease: "easeOut"
                          }
                        }
                      }}
                      whileHover={{ 
                        x: 5,
                        transition: { duration: 0.2 }
                      }}
                    >
                      <span className="text-[#495565] text-sm md:text-base lg:text-sm leading-tight lg:leading-[21px] [font-family:'Segoe_UI_Symbol-Regular',Helvetica] font-normal tracking-[0]">
                        {item.label}
                      </span>
                      <motion.span
                        className={`${item.statusColor} text-sm md:text-base lg:text-sm leading-tight lg:leading-[21px] [font-family:'Segoe_UI_Symbol-Regular',Helvetica] font-normal tracking-[0]`}
                        initial={{ opacity: 0, scale: 0.8 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ delay: index * 0.1 + 0.5 }}
                      >
                        {item.status}
                      </motion.span>
                    </motion.div>
                  ))}
                </motion.div>
              </CardContent>
            </Card>
          </motion.div>
        </motion.div>

        {/* Feature Cards */}
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 lg:gap-7 mb-8 md:mb-12 lg:mb-[45px]"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={{
            visible: {
              transition: {
                staggerChildren: 0.15,
                delayChildren: 0.4
              }
            }
          }}
        >
          {config.featureCards.map((card, index) => {
            const Icon = featureIcons[index].icon;
            return(
            <motion.div
              key={index}
              variants={{
                hidden: { opacity: 0, y: 50, scale: 0.95 },
                visible: {
                  opacity: 1,
                  y: 0,
                  scale: 1,
                  transition: {
                    duration: 0.8,
                    ease: "easeOut"
                  }
                },
                hover: {
                  y: -8,
                  scale: 1.02,
                  transition: {
                    duration: 0.3,
                    ease: "easeInOut"
                  }
                }
              }}
              whileHover="hover"
            >
              <Card className="bg-white rounded-xl lg:rounded-[12.75px] border h-auto">
                <CardContent className="p-6 md:p-8 lg:p-7 flex flex-col items-center text-center">
                  {/* Icon Container */}
                  <motion.div
                    className={`w-12 h-12 md:w-14 md:h-14 lg:w-14 lg:h-14 flex items-center justify-center ${card.bgColor} rounded-xl lg:rounded-[12.75px] mb-4 md:mb-6`}
                    whileHover={{ 
                      scale: 1.1,
                      rotate: 360,
                      transition: { duration: 0.6 }
                    }}
                    initial={{ rotate: 0, scale: 0.8 }}
                    whileInView={{ rotate: 0, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >
                    <Icon
                      className={`w-6 h-6 md:w-7 md:h-7 ${card.iconColor}`}
                    />
                  </motion.div>

                  {/* Content */}
                  <motion.div 
                    className="flex flex-col gap-3 md:gap-4"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 + 0.2 }}
                  >
                    <h3 className="text-frail-scale-05242098figmasiteebony text-base md:text-lg lg:text-[17.5px] leading-tight lg:leading-[24.5px] [font-family:'Segoe_UI_Symbol-Regular',Helvetica] font-normal tracking-[0]">
                      {card.title}
                    </h3>
                    <p className="text-[#495565] text-sm md:text-base lg:text-[13.8px] leading-relaxed lg:leading-[22.8px] [font-family:'Segoe_UI_Symbol-Regular',Helvetica] font-normal tracking-[0]">
                      {card.description}
                    </p>
                  </motion.div>
                </CardContent>
              </Card>
            </motion.div>
           )})}
        </motion.div>

        {/* CTA Button */}
        <motion.div
          className="flex justify-center"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.5 }}
        >
          <Link href={config.cta.href}>
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <Button className="bg-[#980ffa] hover:bg-[#980ffa]/90 text-white rounded-lg lg:rounded-[6.75px] h-auto px-4 py-2 md:px-6 md:py-3 lg:px-2.5 lg:py-[5px] text-sm md:text-base lg:text-[12.3px] leading-tight lg:leading-[17.5px] [font-family:'Segoe_UI_Symbol-Regular',Helvetica] font-normal tracking-[0]">
                {config.cta.label}
                <motion.div
                  animate={{ x: [0, 3, 0] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <ArrowRightIcon className="w-4 h-4 md:w-5 md:h-5 lg:w-3.5 lg:h-3.5 ml-2" />
                </motion.div>
              </Button>
            </motion.div>
          </Link>
        </motion.div>
      </div>
    </motion.section>
  );
};